import { useCallback, useState } from 'react';
import { User } from '../../../types/user.types';

export interface UseUsersTableProps {
  users: User[];
  selectedUsers: string[];
  setSelectedUsers: React.Dispatch<React.SetStateAction<string[]>>;
  onViewUser?: (id: string) => void;
  onEditUser?: (id: string) => void;
  onDeleteUser?: (id: string) => void;
  onBlockUserDirect?: (id: string) => Promise<void>;
  onUnblockUserDirect?: (id: string) => Promise<void>;
}

export const useUsersTable = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  users,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selectedUsers,
  setSelectedUsers,
  onViewUser,
  onEditUser,
  onDeleteUser,
  onBlockUserDirect,
  onUnblockUserDirect,
}: UseUsersTableProps) => {
  const [blockingUserId, setBlockingUserId] = useState<string | null>(null);

  const getStatusDisplay = useCallback((isActive: boolean) => {
    return isActive ? 'Activo' : 'Inactivo';
  }, []);

  const getStatusColor = useCallback((isActive: boolean) => {
    return isActive ? 'success' : 'default';
  }, []);

  const getLockedDisplay = useCallback((user: User) => {
    return user.lockedAt ? 'Bloqueado' : 'Normal';
  }, []);

  const getLockedColor = useCallback((user: User) => {
    return user.lockedAt ? 'error' : 'success';
  }, []);

  const formatDate = useCallback((dateString: string) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  }, []);

  const getUserName = useCallback((user: User) => {
    const fullName = `${user.firstName} ${user.lastName}`.trim();
    return fullName || user.email;
  }, []);

  const getRoleNames = useCallback((user: User) => {
    return user.roles?.map((role) => role.name) || [];
  }, []);

  const handleViewUser = useCallback(
    (id: string) => {
      onViewUser?.(id);
    },
    [onViewUser],
  );

  const handleEditUser = useCallback(
    (id: string) => {
      onEditUser?.(id);
    },
    [onEditUser],
  );

  const handleDeleteUser = useCallback(
    (id: string) => {
      onDeleteUser?.(id);
    },
    [onDeleteUser],
  );

  const handleSwitchChange = useCallback(
    async (user: User, newValue: boolean) => {
      setBlockingUserId(user.id);

      try {
        const isLocked = !!user.lockedAt;
        if (newValue && isLocked) {
          await onUnblockUserDirect?.(user.id);
        }
        if (!newValue && !isLocked) {
          await onBlockUserDirect?.(user.id);
        }
      } finally {
        setBlockingUserId(null);
      }
    },
    [onBlockUserDirect, onUnblockUserDirect],
  );

  const isBlocking = useCallback((userId: string) => blockingUserId === userId, [blockingUserId]);

  const handleSelectItems = useCallback(
    (items: (string | number)[]) => setSelectedUsers(items as string[]),
    [setSelectedUsers],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const canDeleteUser = useCallback((user: User) => {
    return true;
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const canEditUser = useCallback((user: User) => {
    return true;
  }, []);

  return {
    getStatusDisplay,
    getStatusColor,
    getLockedDisplay,
    getLockedColor,
    formatDate,
    getUserName,
    getRoleNames,
    handleViewUser,
    handleEditUser,
    handleDeleteUser,
    handleSwitchChange,
    handleSelectItems,
    isBlocking,
    canDeleteUser,
    canEditUser,
  };
};
